// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import { collection, query, where, doc, getDoc, getDocs } from "firebase/firestore";

export async function getUserInfo(firestore, uid)  {
    const userInfo = {};

    // Fetch user company & language
    let userDocSnap;
    try {
        userDocSnap = await getDoc(doc(firestore, "users", uid));
    } catch (error) {
        console.error("Error fetching user info:", error);
        return {};
    }
    
    if (!userDocSnap.exists()) {
        return;
    }
    userInfo["info"] = userDocSnap.data();
    const company = userDocSnap.data().company;

    // Fetch company display name
    let clientDocSnap;
    try {
        clientDocSnap = await getDoc(doc(firestore, "clients", company));
    } catch (error) {
        console.error("Error fetching client info:", error);
        return {};
    }
    
    if (clientDocSnap.exists()) {
        userInfo["company"] = clientDocSnap.data();
    }

    // Fetch device
    const deviceQuery = query(collection(firestore, "clients", company, "devices"), where("client", "==", company));
    let querySnapshot;
    try {
        querySnapshot = await getDocs(deviceQuery);
    } catch (error) {
        console.error("Error fetching devices:", error);
        return {};
    }
    
    if (querySnapshot.empty) {
        userInfo["device_id"] = null;
        return userInfo;
    }
    userInfo["devices"] = {};
    for (const doc of querySnapshot.docs) {
        userInfo["devices"][doc.id] = doc.data();
    }

    if (userInfo.info.selected_device_id && userInfo.info.selected_device_id in userInfo.devices) {
        userInfo["device_id"] = userInfo.info.selected_device_id;
    } else {
        userInfo["device_id"] = querySnapshot.docs[0].data().device_id;
    }

    return userInfo;
}

export async function getLabelsets(firestore, company) {
    const labelsets = {};
    
    let labelsetsSnapshot;
    try {
        labelsetsSnapshot = await getDocs(collection(firestore, "clients", company, "labelsets"));
    } catch (error) {
        console.error("Error fetching labelsets:", error);
        return {};
    }
    
    labelsetsSnapshot.forEach((doc) => {
        labelsets[doc.id] = {...doc.data()};
    });
    
    return labelsets;
}

export async function getParametersConfig(firestore) {
    let snapshot;
    try {
        snapshot = await getDoc(doc(firestore, "settings", "device_parameters_config"));
    } catch (error) {
        console.error("Error fetching parameters config:", error);
        return {};
    }
    
    if (!snapshot.exists()) {
        return;
    }
    return snapshot.data();
}

export async function getDeliverySettings(firestore, company) {
    let deliverySettings = {};
    
    let deliverySettingsSnapshot;
    try {
        deliverySettingsSnapshot = await getDoc(doc(firestore, "clients", company, "client_settings", "deliveries"));
    } catch (error) {
        console.error("Error fetching delivery settings:", error);
        return {};
    }
    
    if (deliverySettingsSnapshot.exists()) {
        deliverySettings = deliverySettingsSnapshot.data();
    }

    return deliverySettings;
}

export async function getClaimSettings(firestore, company) {
    let claimSettings = {};
    
    let claimSettingsSnapshot;
    try {
        claimSettingsSnapshot = await getDoc(doc(firestore, "clients", company, "client_settings", "claim_settings"));
    } catch (error) {
        console.error("Error fetching claim settings:", error);
        return {};
    }
    
    if (claimSettingsSnapshot.exists()) {
        claimSettings = claimSettingsSnapshot.data();
    }

    return claimSettings;
}

export async function getBaleFeedSettings(firestore, company) {
    let baleFeedSettings = {};
    
    let baleFeedSettingsSnapshot;
    try {
        baleFeedSettingsSnapshot = await getDoc(doc(firestore, "clients", company, "client_settings", "bale_feed_settings"));
    } catch (error) {
        console.error("Error fetching bale feed settings:", error);
        return {};
    }
    
    if (baleFeedSettingsSnapshot.exists()) {
        baleFeedSettings = baleFeedSettingsSnapshot.data();
    }

    return baleFeedSettings;
}

export async function getDashboardSettings(firestore, company) {
    let dashboardSettings = {};
    
    let dashboardSettingsSnapshot;
    try {
        dashboardSettingsSnapshot = await getDoc(doc(firestore, "clients", company, "client_settings", "dashboard_settings"));
    } catch (error) {
        console.error("Error fetching dashboard settings:", error);
        return {};
    }
    
    if (dashboardSettingsSnapshot.exists()) {
        dashboardSettings = dashboardSettingsSnapshot.data();
    }
    
    return dashboardSettings;
}

export async function getRequiredLicenseAgreements(firestore) {
    const q = query(
        collection(firestore, "legal", "client_agreements", "license_agreements"),
        where("required", "==", true),
    );
    
    let snapshot;
    try {
        snapshot = await getDocs(q);
    } catch (error) {
        console.error("Error fetching required license agreements:", error);
        return [];
    }
    
    const requiredLicenses = [];
    snapshot.forEach((doc) => {
        requiredLicenses.push(doc.id);
    });

    return requiredLicenses;
}

export async function getSuppliers(firestore, company) {
    let suppliers = {};
    
    let suppliersSnapshot;
    try {
        suppliersSnapshot = await getDoc(doc(firestore, "clients", company, "client_collections", "suppliers"));
    } catch (error) {
        console.error("Error fetching suppliers:", error);
        return {};
    }
    
    if (suppliersSnapshot.exists()) {
        suppliers = suppliersSnapshot.data()?.summary || {};
    }

    return suppliers;
}